/**
 * DP THRUSTER LAB — DP101 chapter 4. Where a unit may not point, and which number you may claim.
 *
 * ── TWO LESSONS, AND BOTH ARE ABOUT REFUSING AN EASY NUMBER ───────────────────────────────────
 * §4: thrusters do not simply add. Point one azimuth so its wash passes through another's inflow
 * and you degrade BOTH — "a loss on both sides, and it is directional: fine on most headings,
 * severe on a few". So allocation carries forbidden sectors rather than trusting geometry.
 * §5: a rating is the maker's, under the maker's conditions. What a DP calculation may claim comes
 * from the vessel's own documentation or model tests.
 *
 * ── HER FORBIDDEN ARCS COME WITH THEIR REASONS, WHICH IS THE WHOLE POINT ──────────────────────
 * Each azimuth declares `forbidden_deg` AND `forbidden_why`. A sector without a reason is a number
 * somebody must take on trust; a sector that says "firing into the starboard unit and into the
 * skeg" can be argued with. This lab shows the reason beside the arc for exactly that reason.
 *
 * ── AND THE THRUST COEFFICIENTS DECLARE THEIR OWN PROVENANCE ──────────────────────────────────
 * `thrust_kg_per_kw` carries a `_basis` per type, and they are not the same kind of fact: the
 * tunnel figure is CITED to a real 79 m PSV specification with the arithmetic shown, while the
 * azimuth figure says in its own words "the figure is ours and is not a measurement". Showing them
 * side by side is chapter 5's discipline applied to chapter 4's numbers — and it is the honest
 * answer to a learner who asks where 13.5 kg/kW came from.
 */
import { useState, useEffect } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused, absent } from '../../../packages/ankr-provenance/src/provenance.mjs';
import { FORBIDDEN_IS, INTERACTION_IS, RATINGS_ARE, CLAIMABLE_IS, THREE_NUMBERS, SOURCE_CH4, WHY_NOT_SCORED } from './dplabs.mjs';

const R = 86, CX = 100, CY = 100;

export default function DpThrusterLab() {
  const [model, setModel] = useState(null);
  const [sel, setSel] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { let live = true; fetch('/api/dp/model').then((r) => r.json()).then((m) => { if (live) { setModel(m); setSel((m.thrusters || []).find((t) => t.forbidden_deg)?.id || (m.thrusters || [])[0]?.id); } }).catch((e) => live && setErr(String(e))); return () => { live = false; }; }, []);

  if (err) return <div className="g-glass shell-panel lab"><p className="g-body"><Value of={refused(`Her declared thrusters could not be read: ${err}. Nothing is drawn.`)} /></p></div>;
  if (!model) return <div className="g-glass shell-panel lab"><p className="g-body g-dim">Loading her declared thrusters…</p></div>;

  const t = (model.thrusters || []).find((x) => x.id === sel) || (model.thrusters || [])[0];
  const arcs = t && t.forbidden_deg ? t.forbidden_deg : [];
  const kgkw = model.thrust_kg_per_kw || {};
  /* Delivered thrust is the arithmetic the coefficient exists for, shown rather than asserted:
     kW x kg/kW / 1000 = tonnes. A learner can check it on paper, which is the point. */
  const tonnes = t ? (t.kw * (kgkw[t.type] || 0) / 1000) : null;
  const sector = (a0, a1) => {
    const p = (a) => { const r = (a - 90) * Math.PI / 180; return `${(CX + R * Math.cos(r)).toFixed(1)},${(CY + R * Math.sin(r)).toFixed(1)}`; };
    const large = ((a1 - a0 + 360) % 360) > 180 ? 1 : 0;
    return `M ${CX},${CY} L ${p(a0)} A ${R},${R} 0 ${large} 1 ${p(a1)} Z`;
  };

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Thruster bench · forbidden arcs, and the number you may claim</div>
      <h2 className="g-h2 lab-h">Two 2,000 kW units are not twice one.</h2>

      <p className="g-body"><Value of={cited(INTERACTION_IS, SOURCE_CH4)} /></p>
      <p className="g-body"><Value of={cited(FORBIDDEN_IS, SOURCE_CH4)} /></p>

      <div className="lab-task">
        <div className="g-label">Her declared units. Those with a forbidden sector say why.</div>
        <div className="lab-task-head dpf-picks">
          {(model.thrusters || []).map((x) => (
            <button key={x.id} className={`g-btn ${x.id === sel ? 'dpb-on' : ''} ${x.forbidden_deg ? 'dpf-worst' : ''}`} onClick={() => setSel(x.id)}>{x.id}</button>
          ))}
        </div>

        {t && (
          <div className="dpt-unit">
            <svg viewBox="0 0 200 200" className="dpp-plot" role="img" aria-label={`${t.id} forbidden sectors`}>
              <circle cx={CX} cy={CY} r={R} className="dpp-grid" />
              <line x1={CX} y1={CY - R} x2={CX} y2={CY + R} className="dpp-grid" />
              <line x1={CX - R} y1={CY} x2={CX + R} y2={CY} className="dpp-grid" />
              {arcs.map(([a0, a1], i) => <path key={i} d={sector(a0, a1)} className="dpt-forbidden" />)}
              <text x={CX} y={12} className="dpp-lab">000 — ahead</text>
            </svg>
            <div className="dpt-facts">
              <p className="g-body"><b>{t.id}</b> <span className="g-dim">{t.type}, on the {t.switchboard} board, {t.x} m from midships</span></p>
              {arcs.length ? (
                <>
                  <p className="g-body">Forbidden: <Value of={computed(arcs.map(([a, b]) => `${a}°–${b}°`).join(', '))} /></p>
                  <p className="g-body">Because: <Value of={computed(t.forbidden_why)} /></p>
                </>
              ) : (
                <p className="g-body">
                  <Value of={absent()} /> No forbidden sector — a tunnel unit fires athwartships through the hull and has no arc to choose.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── THREE NUMBERS, NOT ONE ── */}
      <div className="lab-task dpb-verdict">
        <div className="g-label">Installed power, delivered thrust, and where each figure comes from</div>
        <p className="g-body"><Value of={cited(RATINGS_ARE, SOURCE_CH4)} /> <Value of={cited(CLAIMABLE_IS, SOURCE_CH4)} /></p>
        {t && (
          <>
            <p className="g-body">
              Installed: <Value of={computed(`${t.kw} kW`)} /> · delivered at{' '}
              {kgkw[t.type] ? <Value of={computed(`${kgkw[t.type]} kg/kW`)} /> : <Value of={absent()} />}{' '}
              = {tonnes ? <Value of={computed(`${tonnes.toFixed(1)} t`)} /> : <Value of={refused('no coefficient declared for this type')} />}
            </p>
            {kgkw[`${t.type}_basis`]
              ? <p className="g-body g-dim"><b>Where that coefficient comes from:</b> {kgkw[`${t.type}_basis`]}</p>
              : <p className="g-body"><Value of={absent()} /> This type declares no basis for its coefficient.</p>}
          </>
        )}
        <p className="g-body g-dim">
          Notice that her three coefficients are not the same KIND of fact. One is cited to a real
          vessel's specification with the arithmetic shown; the others say in their own words that the
          figure is ANKR's and is not a measurement. <Value of={cited(THREE_NUMBERS, SOURCE_CH4)} />
        </p>
      </div>

      <p className="g-body g-dim lab-foot">{WHY_NOT_SCORED}</p>
      <p className="g-body g-dim"><Value of={absent()} /> Nothing here is an attestation.</p>
    </div>
  );
}
